import React, { useState, useEffect, useRef, useCallback } from 'react';
import browserGuardService from '../services/browserGuard';
import { gcExplorerUrl } from '../config';
import { extractSignals } from '../utils/geoSignals';
import { REASON_CODES, reasonName } from '../utils/reasonCodes';
import ABTest from './ABTest';
import './DebugPanel.css';

function formatDuration(ms) {
  if (ms == null || ms < 0) return '--';
  return `${Math.ceil(ms / 1000)}s`;
}

function formatInterval(ms) {
  if (!ms) return '--';
  return `${Math.round(ms / 1000)}s`;
}

const EVENT_LABELS = {
  success:   { label: 'PASS',     color: '#3fb950' },
  denied:    { label: 'DENIED',   color: '#f85149' },
  sdk_error: { label: 'SDK ERR',  color: '#d29922' },
  ux_hint:   { label: 'UX HINT',  color: '#58a6ff' },
};

const TYPE_FILTERS   = ['success', 'denied', 'sdk_error', 'ux_hint'];
const REASON_FILTERS = REASON_CODES.slice(0, 8).map((r) => r.name);

// Classify a requestGeolocation() result for the batch tally.
function classifyResult(r) {
  if (!r) return 'fail';
  if (r.superseded) return 'skipped';
  if (r.success && (r.error_code === 0 || r.error_code === undefined || r.failover)) return 'pass';
  return 'fail';
}

export default function DebugPanel({ isOpen, onClose, geoData }) {
  const [countdown, setCountdown] = useState(null);
  const [snap, setSnap] = useState({});
  const [log, setLog] = useState(() => browserGuardService.geoEventLog || []);
  const [activeTypes,   setActiveTypes]   = useState(new Set());
  const [activeReasons, setActiveReasons] = useState(new Set());
  const [filtersOpen,   setFiltersOpen]   = useState(false);
  const [abOpen,        setAbOpen]        = useState(false); // A/B section collapsed by default
  const [expandedRaw,   setExpandedRaw]   = useState(new Set()); // log entry ids showing full JSON

  const toggleRaw = (key) => setExpandedRaw(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // ── Tester controls state ──────────────────────────────────────
  const [reason, setReason]       = useState('1');        // reason_code (select; from the GeoComply sheet)
  const [reasonText, setReasonText] = useState('');       // free-text reason (SDK 'reason'), e.g. VPN provider
  const [batchCount, setBatchCount]     = useState(5);    // X checks
  const [batchInterval, setBatchInterval] = useState(10); // Y seconds apart
  const [singleBusy, setSingleBusy] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [progress, setProgress] = useState(null);         // { done, total, pass, fail, skipped, phase }
  const [ctrlNote, setCtrlNote] = useState(null);
  const [copied, setCopied] = useState(false);
  const [nextCheckAt, setNextCheckAt] = useState(null);   // timestamp the next batch check fires
  const [batchCountdown, setBatchCountdown] = useState(null); // ms remaining until next check
  const [batchGapMs, setBatchGapMs] = useState(0);        // configured gap, for the wait sub-bar
  const stopRef = useRef(false);
  const waitTimerRef = useRef(null);
  const resolveWaitRef = useRef(null);
  const copyTimerRef = useRef(null);

  const toggleType = (t) => setActiveTypes(prev => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });

  const toggleReason = (r) => setActiveReasons(prev => {
    const next = new Set(prev);
    next.has(r) ? next.delete(r) : next.add(r);
    return next;
  });

  const filteredLog = log.filter(e => {
    if (activeTypes.size   > 0 && !activeTypes.has(e.type))       return false;
    if (activeReasons.size > 0 && !activeReasons.has(e.reasonName)) return false;
    return true;
  });

  useEffect(() => {
    const tick = () => {
      const svc = browserGuardService;
      const now = Date.now();
      let timeLeft = null;
      if (svc.timerId && svc.timerStartTime && svc.timerInterval) {
        timeLeft = Math.max(0, svc.timerInterval - (now - svc.timerStartTime));
      }
      setCountdown(timeLeft);
      const lastResp = svc.lastGeoResponse || {};
      setSnap({
        isInitialized:        svc.isInitialized,
        isMyIpServiceRunning: svc.isMyIpServiceRunning,
        timerInterval:        svc.timerInterval,
        lastReasonCode:       svc.lastReasonCode,
        userId:               svc.userId,
        license:              svc.license ? svc.license.substring(0, 24) + '…' : null,
        geolocateIn:          lastResp.geolocate_in ?? null,
        bufferTime:           lastResp.buffer_time   ?? null,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const unsub = browserGuardService.subscribeDebug((event) => {
      setLog(prev => [event, ...prev].slice(0, 50));
    });
    return unsub;
  }, []);

  // Cleanup any pending batch / copy timers on unmount.
  useEffect(() => () => {
    stopRef.current = true;
    if (waitTimerRef.current) clearTimeout(waitTimerRef.current);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  // Clear the inter-check wait, resolving its promise immediately so a Stop (or
  // teardown) doesn't leave the batch loop awaiting forever.
  const clearWait = useCallback(() => {
    if (waitTimerRef.current) { clearTimeout(waitTimerRef.current); waitTimerRef.current = null; }
    if (resolveWaitRef.current) { const r = resolveWaitRef.current; resolveWaitRef.current = null; r(); }
  }, []);

  // Wait `ms` between checks; resolvable early via clearWait() (used by Stop).
  const cancellableWait = useCallback((ms) => new Promise((resolve) => {
    resolveWaitRef.current = resolve;
    waitTimerRef.current = setTimeout(() => {
      waitTimerRef.current = null;
      resolveWaitRef.current = null;
      resolve();
    }, ms);
  }), []);

  // Tick the "next check in Ns" countdown while waiting between batch checks.
  useEffect(() => {
    if (!nextCheckAt) { setBatchCountdown(null); return; }
    const upd = () => setBatchCountdown(Math.max(0, nextCheckAt - Date.now()));
    upd();
    const id = setInterval(upd, 200);
    return () => clearInterval(id);
  }, [nextCheckAt]);

  const runOnce = useCallback(async (reasonCode) => {
    if (!browserGuardService.isInitialized) {
      // Panel is normally opened post-login (SDK already initialized). Re-init
      // defensively if a tester opened it before the first check landed.
      if (browserGuardService.userId) {
        await browserGuardService.initialize(browserGuardService.userId);
      } else {
        throw new Error('BrowserGuard not initialized — log in first');
      }
    }
    return browserGuardService.requestGeolocation(reasonCode);
  }, []);

  const handleRunNow = useCallback(async () => {
    if (singleBusy || batchRunning) return;
    setCtrlNote(null);
    setSingleBusy(true);
    browserGuardService.reasonText = reasonText.trim() || null;
    try {
      const r = await runOnce(reason);
      const cls = classifyResult(r);
      setCtrlNote(cls === 'pass' ? 'Check passed ✓'
        : cls === 'skipped' ? 'Superseded by another check'
        : `Check failed${r?.error_code ? ` [${r.error_code}]` : ''}`);
    } catch (e) {
      setCtrlNote(e.message || 'Check failed');
    } finally {
      setSingleBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason, reasonText, runOnce, singleBusy, batchRunning]);

  const handleStartBatch = useCallback(async () => {
    if (batchRunning) return;
    const total = Math.max(1, Math.min(100, parseInt(batchCount, 10) || 1));
    const gapMs = Math.max(0, (parseFloat(batchInterval) || 0)) * 1000;
    const rc = reason;
    browserGuardService.reasonText = reasonText.trim() || null;
    setCtrlNote(null);
    stopRef.current = false;
    setBatchRunning(true);
    setBatchGapMs(gapMs);
    let pass = 0, fail = 0, skipped = 0;
    setProgress({ done: 0, total, pass, fail, skipped, phase: 'running' });
    try {
      for (let i = 0; i < total; i++) {
        if (stopRef.current) break;
        const r = await runOnce(rc);
        const cls = classifyResult(r);
        if (cls === 'pass') pass++; else if (cls === 'skipped') skipped++; else fail++;
        setProgress({ done: i + 1, total, pass, fail, skipped, phase: 'running' });
        if (i < total - 1 && !stopRef.current && gapMs > 0) {
          setProgress({ done: i + 1, total, pass, fail, skipped, phase: 'waiting' });
          setNextCheckAt(Date.now() + gapMs);
          await cancellableWait(gapMs);
          setNextCheckAt(null);
        }
      }
    } catch (e) {
      setCtrlNote(e.message || 'Batch stopped on error');
    } finally {
      clearWait();
      setNextCheckAt(null);
      setProgress(prev => prev ? { ...prev, phase: stopRef.current ? 'stopped' : 'done' } : null);
      setBatchRunning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRunning, batchCount, batchInterval, reason, reasonText, runOnce, cancellableWait, clearWait]);

  const handleStopBatch = useCallback(() => {
    stopRef.current = true;
    clearWait();           // resolve the pending wait immediately so the loop breaks now
    setNextCheckAt(null);
  }, [clearWait]);

  const handleClearLog = useCallback(() => {
    browserGuardService.geoEventLog = [];
    setLog([]);
  }, []);

  const handleCopyLog = useCallback(() => {
    try {
      navigator.clipboard?.writeText(JSON.stringify(filteredLog, null, 2));
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  }, [filteredLog]);

  const lastReasonName = snap.lastReasonCode ? reasonName(snap.lastReasonCode) : '--';

  const svc = browserGuardService;
  const last = svc.lastGeoResponse || {};
  // lastGeoResponse is only set on a PASS. So the panel also falls back to the
  // most recent log event (ev) — which now carries partial signals even on a
  // denial — so the LOCATION panel never shows "--" while the log has values.
  const ev = log[0] || {};
  const ip    = last.ipAddress || (typeof last.ip === 'object' ? (last.ip?.ipaddress || last.ip?.address) : last.ip) || geoData?.ip || ev.ip || '--';
  const lat   = last.latitude  ?? geoData?.latitude  ?? ev.lat ?? null;
  const lon   = last.longitude ?? geoData?.longitude ?? ev.lon ?? null;
  const acc   = last.accuracy  ?? last.wifi?.acc ?? (ev.raw && (ev.raw.accuracy ?? ev.raw.wifi?.acc)) ?? null;
  const city  = last.city      || geoData?.city  || ev.city  || '--';
  const state = last.state_code || geoData?.state || ev.state || '--';

  const busy = singleBusy || batchRunning;

  return (
    <div className={`debug-panel ${isOpen ? 'debug-panel--open' : ''}`}>
      <div className="debug-panel-inner">

        {/* ── Panel Header ──────────────────────────────────────────────── */}
        <div className="debug-panel-titlebar">
          <span className="debug-panel-title">Behind the Scenes</span>
          <button className="debug-panel-close" onClick={onClose}>×</button>
        </div>

        <div className="debug-panel-body">

        {/* ── Tester Controls ───────────────────────────────────────────── */}
        <div className="debug-section debug-controls">
          <div className="debug-section-title">Tester Controls</div>

          {/* Reason code — full GeoComply list (SDK reason_code) */}
          <div className="debug-ctrl-row">
            <label className="debug-ctrl-label">Reason code</label>
            <select
              className="debug-ctrl-select debug-ctrl-select--wide"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              title="GeoComply reason_code sent with the check"
            >
              {REASON_CODES.map((o) => (
                <option key={o.code} value={o.code}>{`${o.code} — ${o.name}`}</option>
              ))}
            </select>
          </div>

          {/* Reason — free text (SDK reason), e.g. the VPN provider */}
          <div className="debug-ctrl-row">
            <label className="debug-ctrl-label">Reason</label>
            <input
              className="debug-ctrl-text"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              disabled={busy}
              placeholder="free text — e.g. ExpressVPN, NordVPN, normal, GLI test"
              title="Free-text reason sent with the check (SDK 'reason') and shown in the log"
            />
            <button
              className="debug-ctrl-btn debug-ctrl-btn--primary"
              onClick={handleRunNow}
              disabled={busy}
            >
              {singleBusy ? 'Checking…' : '▶ Run check now'}
            </button>
          </div>

          {/* Batch runner */}
          <div className="debug-ctrl-row">
            <label className="debug-ctrl-label">Run</label>
            <input
              type="number" min="1" max="100"
              className="debug-ctrl-num"
              value={batchCount}
              onChange={(e) => setBatchCount(e.target.value)}
              disabled={batchRunning}
            />
            <span className="debug-ctrl-inline">checks,</span>
            <input
              type="number" min="0" max="3600" step="1"
              className="debug-ctrl-num"
              value={batchInterval}
              onChange={(e) => setBatchInterval(e.target.value)}
              disabled={batchRunning}
            />
            <span className="debug-ctrl-inline">s apart</span>
            {!batchRunning ? (
              <button
                className="debug-ctrl-btn"
                onClick={handleStartBatch}
                disabled={singleBusy}
              >
                Start
              </button>
            ) : (
              <button className="debug-ctrl-btn debug-ctrl-btn--stop" onClick={handleStopBatch}>
                ■ Stop
              </button>
            )}
          </div>

          {/* Batch progress */}
          {progress && (
            <div className="debug-ctrl-progress">
              <div className="debug-ctrl-progress-bar">
                <div
                  className="debug-ctrl-progress-fill"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
              <div className="debug-ctrl-progress-text">
                <span>
                  {progress.phase === 'waiting' ? 'Waiting' :
                   progress.phase === 'stopped' ? 'Stopped' :
                   progress.phase === 'done' ? 'Complete' : 'Running'}
                  {' '}{progress.done}/{progress.total}
                </span>
                <span className="debug-ctrl-tally">
                  <span className="debug-tally-pass">{progress.pass} pass</span>
                  <span className="debug-tally-fail">{progress.fail} fail</span>
                  {progress.skipped > 0 && <span className="debug-tally-skip">{progress.skipped} skip</span>}
                </span>
              </div>

              {/* Countdown to the next check while waiting between batch checks */}
              {progress.phase === 'waiting' && batchCountdown != null && (
                <div className="debug-ctrl-next">
                  <div className="debug-ctrl-next-head">
                    <span className="debug-ctrl-next-label">⏱ Next check in</span>
                    <span className="debug-ctrl-next-val">{Math.ceil(batchCountdown / 1000)}s</span>
                  </div>
                  <div className="debug-ctrl-next-bar">
                    <div
                      className="debug-ctrl-next-fill"
                      style={{ width: `${batchGapMs ? Math.min(100, (1 - batchCountdown / batchGapMs) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {ctrlNote && <div className="debug-ctrl-note">{ctrlNote}</div>}

          <div className="debug-ctrl-row debug-ctrl-row--utils">
            <button className="debug-ctrl-link" onClick={handleClearLog}>Clear log</button>
            <button className="debug-ctrl-link" onClick={handleCopyLog}>{copied ? 'Copied ✓' : 'Copy log JSON'}</button>
          </div>
        </div>

        {/* ── A/B Comparison Test (collapsed by default) ────────────────── */}
        <div className="debug-section">
          <button className="debug-collapse-head" onClick={() => setAbOpen((o) => !o)}>
            <span className="debug-section-title debug-section-title--inline">A / B Comparison Test</span>
            <span className="debug-collapse-arrow">{abOpen ? '▾' : '▸'}</span>
          </button>
          {/* kept mounted (hidden) so a captured sample survives collapse */}
          <div style={{ display: abOpen ? 'block' : 'none', marginTop: 12 }}>
            <ABTest />
          </div>
        </div>

        {/* ── Detection & Signals (latest check) ────────────────────────── */}
        <SignalsBreakdown event={log[0]} />

        {/* ── Top grid: Location | Timer + SDK ─────────────────────────── */}
        <div className="debug-top-grid">

          {/* Location */}
          <div className="debug-section">
            <div className="debug-section-title">Location</div>
            <div className="debug-row">
              <span className="debug-key">City / State</span>
              <span className="debug-val">{city !== '--' ? `${city}, ${state}` : '--'}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">Coordinates</span>
              <span className="debug-val debug-val--mono">
                {lat != null ? `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}` : '--'}
              </span>
            </div>
            <div className="debug-row">
              <span className="debug-key">Accuracy</span>
              <span className="debug-val debug-val--mono">{acc != null && acc !== '' ? `±${acc}m` : '--'}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">IP Address</span>
              <span className="debug-val debug-val--mono">{ip}</span>
            </div>
          </div>

          {/* Timer */}
          <div className="debug-section">
            <div className="debug-section-title">Geo Check Timer</div>
            <div className="debug-countdown">
              {countdown != null ? formatDuration(countdown) : 'No timer'}
            </div>
            <div className="debug-row">
              <span className="debug-key">Fires in</span>
              <span className="debug-val">{formatInterval(snap.timerInterval)}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">GeoComply interval</span>
              <span className="debug-val debug-val--mono">{snap.geolocateIn != null ? `${snap.geolocateIn}s` : '--'}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">Buffer</span>
              <span className="debug-val debug-val--mono">{snap.bufferTime != null ? `${snap.bufferTime}s` : '--'}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">Last Reason</span>
              <span className="debug-val">{lastReasonName}</span>
            </div>
          </div>

          {/* SDK */}
          <div className="debug-section debug-section--span2">
            <div className="debug-section-title">SDK State</div>
            <div className="debug-row">
              <span className="debug-key">Initialized</span>
              <span className={`debug-val ${snap.isInitialized ? 'debug-val--ok' : 'debug-val--err'}`}>
                {snap.isInitialized ? 'YES' : 'NO'}
              </span>
            </div>
            <div className="debug-row">
              <span className="debug-key">IP Change Detection</span>
              <span className={`debug-val ${snap.isMyIpServiceRunning ? 'debug-val--ok' : ''}`}>
                {snap.isMyIpServiceRunning ? 'YES' : 'no'}
              </span>
            </div>
            <div className="debug-row">
              <span className="debug-key">User ID</span>
              <span className="debug-val">{snap.userId || '--'}</span>
            </div>
            <div className="debug-row">
              <span className="debug-key">License</span>
              <span className="debug-val debug-val--mono">{snap.license || '--'}</span>
            </div>
          </div>
        </div>

        {/* ── Geo Event Log ─────────────────────────────────────────────── */}
        <div className="debug-section debug-section--log">
          <div className="debug-log-topbar">
            <div className="debug-log-topbar-row">
              <span className="debug-section-title" style={{ margin: 0, padding: 0, border: 'none' }}>Geo Event Log</span>
              <div className="debug-log-topbar-actions">
                {(activeTypes.size > 0 || activeReasons.size > 0) && (
                  <span className="debug-filter-active-count">{activeTypes.size + activeReasons.size} active</span>
                )}
                <button className={`debug-filter-toggle ${filtersOpen ? 'debug-filter-toggle--open' : ''}`} onClick={() => setFiltersOpen(p => !p)}>
                  Filter
                  <span className="debug-filter-toggle-arrow">{filtersOpen ? '▲' : '▼'}</span>
                </button>
                {(activeTypes.size > 0 || activeReasons.size > 0) && (
                  <button className="debug-filter-clear" onClick={() => { setActiveTypes(new Set()); setActiveReasons(new Set()); }}>
                    Clear
                  </button>
                )}
              </div>
            </div>

            {filtersOpen && (
              <div className="debug-filters">
                <div className="debug-filter-group">
                  {TYPE_FILTERS.map(t => {
                    const meta = EVENT_LABELS[t];
                    return (
                      <button
                        key={t}
                        className={`debug-filter-btn ${activeTypes.has(t) ? 'debug-filter-btn--active' : ''}`}
                        style={ activeTypes.has(t) ? { borderColor: meta.color, color: meta.color, background: meta.color + '18' } : {} }
                        onClick={() => toggleType(t)}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
                <div className="debug-filter-divider" />
                <div className="debug-filter-group">
                  {REASON_FILTERS.map(r => (
                    <button
                      key={r}
                      className={`debug-filter-btn ${activeReasons.has(r) ? 'debug-filter-btn--active debug-filter-btn--reason' : ''}`}
                      onClick={() => toggleReason(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {filteredLog.length === 0 ? (
            <div className="debug-log-empty">{log.length === 0 ? 'No events yet' : 'No events match filters'}</div>
          ) : (
            <div className="debug-log">
              {filteredLog.map((e, i) => {
                const meta = EVENT_LABELS[e.type] || { label: e.type.toUpperCase(), color: '#8b949e' };
                return (
                  <div key={e.id ?? i} className={`debug-log-entry debug-log-entry--${e.type}`}>

                    {/* Header row: time + badge + reason */}
                    <div className="debug-log-header">
                      <span className="debug-log-badge" style={{ color: meta.color, borderColor: meta.color + '55', background: meta.color + '15' }}>
                        {meta.label}
                      </span>
                      {e.reasonName && <span className="debug-log-reason">{e.reasonName}</span>}
                      {e.reasonText && <span className="debug-log-label">🏷 {e.reasonText}</span>}
                      <span className="debug-log-time">{e.time}</span>
                    </div>

                    {/* Success: labeled fields */}
                    {e.type === 'success' && (
                      <div className="debug-log-fields">
                        <div className="debug-log-field">
                          <span className="debug-log-field-key">Location</span>
                          <span className="debug-log-field-val">{e.city || '?'}, {e.state || '?'}</span>
                        </div>
                        {e.ip && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">IP Address</span>
                            <span className="debug-log-field-val debug-log-field-mono">{e.ip}</span>
                          </div>
                        )}
                        {e.lat != null && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">Coordinates</span>
                            <span className="debug-log-field-val debug-log-field-mono">{Number(e.lat).toFixed(5)}, {Number(e.lon).toFixed(5)}</span>
                          </div>
                        )}
                        {e.geolocate_in != null && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">Next Check</span>
                            <span className="debug-log-field-val">in {e.geolocate_in}s</span>
                          </div>
                        )}
                        {e.transaction_id && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">GC Txn ID</span>
                            <a
                              className="debug-log-field-val debug-log-field-mono debug-log-txn-link"
                              href={gcExplorerUrl(e.transaction_id, browserGuardService.operator)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {e.transaction_id}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Denied / SDK error: error + any available values (never blank) */}
                    {(e.type === 'denied' || e.type === 'sdk_error') && (
                      <div className="debug-log-fields">
                        <div className="debug-log-field">
                          <span className="debug-log-field-key">Result</span>
                          <span className="debug-log-field-val debug-log-field-err">FAILED{e.error_code != null ? ` [${e.error_code}]` : ''}</span>
                        </div>
                        {e.error_message && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">Reason</span>
                            <span className="debug-log-field-val debug-log-field-err">{e.error_message}</span>
                          </div>
                        )}
                        {(e.city || e.state) && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">Location</span>
                            <span className="debug-log-field-val">{e.city || '?'}, {e.state || '?'}</span>
                          </div>
                        )}
                        {e.ip && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">IP Address</span>
                            <span className="debug-log-field-val debug-log-field-mono">{e.ip}</span>
                          </div>
                        )}
                        {e.lat != null && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">Coordinates</span>
                            <span className="debug-log-field-val debug-log-field-mono">{Number(e.lat).toFixed(5)}, {Number(e.lon).toFixed(5)}</span>
                          </div>
                        )}
                        {e.transaction_id && (
                          <div className="debug-log-field">
                            <span className="debug-log-field-key">GC Txn ID</span>
                            <a className="debug-log-field-val debug-log-field-mono debug-log-txn-link" href={gcExplorerUrl(e.transaction_id, browserGuardService.operator)} target="_blank" rel="noopener noreferrer">{e.transaction_id}</a>
                          </div>
                        )}
                        {e.troubleshooter && e.troubleshooter.filter(t => t.type === 'message' || !t.type).map((t, ti) => (
                          <React.Fragment key={ti}>
                            {t.rule && t.rule !== 'unknown' && (
                              <div className="debug-log-field">
                                <span className="debug-log-field-key">Rule</span>
                                <span className="debug-log-field-val debug-log-field-mono">{t.rule}</span>
                              </div>
                            )}
                            <div className="debug-log-field">
                              <span className="debug-log-field-key">Message</span>
                              <span className="debug-log-field-val debug-log-field-hint">{t.content}</span>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* UX hint */}
                    {e.type === 'ux_hint' && e.error_message && (
                      <div className="debug-log-fields">
                        <div className="debug-log-field">
                          <span className="debug-log-field-key">Hint</span>
                          <span className="debug-log-field-val" style={{ color: '#58a6ff' }}>{e.error_message}</span>
                        </div>
                      </div>
                    )}

                    {/* Full decrypted response — expandable for any entry that has it */}
                    {e.raw && (() => {
                      const key = e.id ?? i;
                      const open = expandedRaw.has(key);
                      return (
                        <div className="debug-log-raw-wrap">
                          <button className="debug-log-raw-toggle" onClick={() => toggleRaw(key)}>
                            {open ? '▾ Hide full response' : '▸ Show full response'}
                          </button>
                          {open && <pre className="debug-log-raw">{JSON.stringify(e.raw, null, 2)}</pre>}
                        </div>
                      );
                    })()}

                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>{/* end debug-panel-body */}

      </div>
    </div>
  );
}

// ── Detection & Signals (latest check) ─────────────────────────────────────────
const SOURCE_LABELS = { gps: 'GPS', wifi: 'Wi-Fi', ip: 'IP', gsm: 'Cell' };

function SignalsBreakdown({ event }) {
  const sig = extractSignals(event);

  return (
    <div className="debug-section debug-signals">
      <div className="debug-section-title">Detection &amp; Signals — latest check</div>

      {!sig ? (
        <div className="debug-signals-empty">Run a check to see how BrowserGuard located &amp; assessed you.</div>
      ) : (
        <>
          <div className="debug-sig-top">
            <span className={`debug-sig-verdict debug-sig-verdict--${sig.verdict}`}>
              {sig.verdict === 'pass' ? 'PASS' : sig.verdict === 'denied' ? 'DENIED' : sig.verdict === 'error' ? `ERROR ${sig.errorCode ?? ''}` : 'UNKNOWN'}
            </span>
            {sig.primary && <span className="debug-sig-chip">primary: {SOURCE_LABELS[sig.primary] || sig.primary}</span>}
            {sig.connType && <span className="debug-sig-chip">{sig.connType}</span>}
            {sig.proxyscore != null && (
              <span className={`debug-sig-chip ${sig.proxyscore >= 50 ? 'debug-sig-chip--warn' : ''}`}>VPN/proxy score {sig.proxyscore}%</span>
            )}
          </div>

          {/* Per-source accuracy breakdown */}
          {sig.sources.length > 0 && (
            <div className="debug-sig-sources">
              {sig.sources.map((s) => (
                <div key={s.type} className={`debug-sig-src${s.primary ? ' debug-sig-src--primary' : ''}`}>
                  <span className="debug-sig-src-name">{SOURCE_LABELS[s.type] || s.type}</span>
                  <span className="debug-sig-src-meta">
                    {s.err ? <span className="debug-sig-src-err">err {s.err}</span>
                      : s.acc != null ? `±${Math.round(s.acc)}m` : '—'}
                    {s.type === 'ip' && s.ipaddress ? ` · ${s.ipaddress}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {(sig.ipAddress || sig.isp) && (
            <div className="debug-sig-line">
              <span className="debug-sig-key">Network</span>
              <span className="debug-sig-val">{[sig.isp, sig.ipAddress].filter(Boolean).join(' · ') || '—'}</span>
            </div>
          )}

          {/* Risk rules BrowserGuard fired */}
          <div className="debug-sig-risks-title">Anti-fraud rules</div>
          {sig.risks.length === 0 ? (
            <div className="debug-sig-clean">✓ No risk rules fired — clean check</div>
          ) : (
            <div className="debug-sig-risks">
              {sig.risks.map((r) => (
                <div key={r.rule} className="debug-sig-risk">
                  <span className="debug-sig-risk-flag">⚑</span>
                  <span className="debug-sig-risk-label">{r.label}</span>
                  {r.detail && <span className="debug-sig-risk-detail">{r.detail}</span>}
                </div>
              ))}
            </div>
          )}

          {sig.summary && Object.keys(sig.summary).length > 0 && (
            <div className="debug-sig-summary">
              {Object.entries(sig.summary).filter(([, v]) => v).map(([k]) => (
                <span key={k} className="debug-sig-summary-chip">{k.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
